// Extracted from StudentProfile.js's renderUpcomingLessons (Phase 3 of the
// platform-health plan). Pure data-shaping, split out from the render
// function on purpose: it has no React/JSX dependency, so it's testable in
// isolation and safe to move first, before touching the JSX that consumes
// its output.
//
// Builds the current/offset week's day list plus, for each day, the
// deduplicated list of lessons scheduled on it — merging the schedule's
// own upcoming-lessons list with the Supabase lessons list and the legacy
// localStorage lessons list (in that priority order), then collapsing
// anything that looks like the same booking (same date/time/student) down
// to one entry, preferring the more recently-updated or more complete copy.
export function computeWeekSchedule({
  weekOffset,
  upcomingLessons,
  studentLessons,
  studentId,
  localLessons,
  studentIdForLessons,
}) {
  const today = new Date();
  const base = new Date(today);
  base.setDate(base.getDate() + weekOffset * 7);
  const dayIndexMon0 = (base.getDay() + 6) % 7; // Mon=0 .. Sun=6
  const weekStart = new Date(base);
  weekStart.setDate(base.getDate() - dayIndexMon0);
  weekStart.setHours(0, 0, 0, 0);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  const weekEnd = new Date(weekDays[6]);
  weekEnd.setHours(23, 59, 59, 999);

  // upcomingLessons уже содержит все отфильтрованные по дате уроки из расписания
  const allUpcomingLessons = upcomingLessons;

  // Объединяем все upcoming lessons (из расписания) с данными из других источников
  // Priority: allUpcomingLessons (schedule) > studentLessons (Supabase lessons) > novas_lessons_data
  const combinedLessons = [
    ...allUpcomingLessons,
    ...studentLessons.filter(lesson => {
      if (allUpcomingLessons.some(scheduleLesson => scheduleLesson.id === lesson.id)) {
        return false;
      }
      // Уроки в календаре должны иметь время начала и окончания
      const hasTime = (lesson.from || lesson.timeFrom) && (lesson.to || lesson.timeTo);
      if (!hasTime) {
        return false;
      }
      return true;
    }),
    ...localLessons.filter(lesson =>
      !allUpcomingLessons.some(scheduleLesson => scheduleLesson.id === lesson.id) &&
      !studentLessons.some(supabaseLesson => supabaseLesson.id === lesson.id)
    ),
  ].filter(lesson => {
    // Filter by studentId - handle both UUID and slug formats
    const lessonStudentId = String(lesson.studentId || '').trim();
    const currentStudentId = String(studentId || '').trim();
    return lessonStudentId === currentStudentId ||
           lessonStudentId === studentIdForLessons ||
           lessonStudentId === '' ||
           currentStudentId === '';
  });

  // Remove duplicates by date, time, and studentId (not just by ID) — this
  // prevents showing the same lesson multiple times if it was saved with
  // different IDs.
  const uniqueLessons = [];
  const seenLessons = new Map();

  combinedLessons.forEach(lesson => {
    if (!lesson.date) return;

    let normalizedDate = lesson.date;
    try {
      const dateObj = new Date(lesson.date);
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        normalizedDate = `${year}-${month}-${day}`;
      }
    } catch (e) {
      return;
    }

    const normalizedFrom = (lesson.from || lesson.timeFrom || '').trim().replace(/\s+/g, '') || 'no-time';
    const normalizedTo = (lesson.to || lesson.timeTo || '').trim().replace(/\s+/g, '') || 'no-time';
    const normalizedStudentId = String(lesson.studentId || '').trim();

    const lessonKey = `${normalizedDate}_${normalizedFrom}_${normalizedTo}_${normalizedStudentId}`;

    if (!seenLessons.has(lessonKey)) {
      seenLessons.set(lessonKey, uniqueLessons.length);
      uniqueLessons.push(lesson);
    } else {
      const existingIndex = seenLessons.get(lessonKey);
      if (existingIndex !== undefined && existingIndex >= 0) {
        const existing = uniqueLessons[existingIndex];

        const existingTime = existing.updatedAt || existing.createdAt || existing.savedAt || '';
        const lessonTime = lesson.updatedAt || lesson.createdAt || lesson.savedAt || '';

        const existingTimestamp = existingTime ? new Date(existingTime).getTime() : 0;
        const lessonTimestamp = lessonTime ? new Date(lessonTime).getTime() : 0;

        // Prefer lessons with "in_progress" status over "scheduled" for same date/time
        const existingStatus = (existing.status || '').toLowerCase();
        const lessonStatus = (lesson.status || '').toLowerCase();
        const preferExisting = existingStatus === 'in_progress' && lessonStatus === 'scheduled';
        const preferLesson = lessonStatus === 'in_progress' && existingStatus === 'scheduled';

        const existingHasSupabaseId = existing.id && existing.id.includes('-') && existing.id.length > 20;
        const lessonHasSupabaseId = lesson.id && lesson.id.includes('-') && lesson.id.length > 20;

        if (preferLesson ||
            (lessonTimestamp > existingTimestamp && !preferExisting) ||
            (lessonTimestamp === existingTimestamp && lessonHasSupabaseId && !existingHasSupabaseId) ||
            (lessonTimestamp === existingTimestamp && Object.keys(lesson).length > Object.keys(existing).length)) {
          uniqueLessons[existingIndex] = lesson;
        }
        // If preferExisting is true, keep existing lesson (don't replace)
      }
    }
  });

  const lessonsInWeek = uniqueLessons.filter(l => {
    if (!l.date) return false;

    // Уроки в календаре должны иметь время начала и окончания
    const hasTime = (l.from || l.timeFrom) && (l.to || l.timeTo);
    if (!hasTime && !l.allDay) {
      return false;
    }

    const d = new Date(l.date);
    return d >= weekStart && d <= weekEnd;
  });

  const lessonsByDay = weekDays.map(d => {
    const y = d.getFullYear();
    const m = d.getMonth();
    const day = d.getDate();
    return lessonsInWeek
      .filter(l => {
        const ld = l.date ? new Date(l.date) : null;
        return (
          ld &&
          ld.getFullYear() === y &&
          ld.getMonth() === m &&
          ld.getDate() === day
        );
      })
      .sort((a, b) => {
        const at = (a.from || a.timeFrom || '').padStart(5, '0');
        const bt = (b.from || b.timeFrom || '').padStart(5, '0');
        return at.localeCompare(bt);
      });
  });

  return { weekDays, lessonsByDay };
}
